import React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import InputField from "../common/InputField";
import Button from "../common/Button";
import { Mail } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { post } from "../../api/apiService";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

class PhoneInputBoundary extends React.Component<
  { children: React.ReactNode },
  { errKey: number }
> {
  state = { errKey: 0 };
  static getDerivedStateFromError() { return {}; }
  componentDidCatch() { this.setState(s => ({ errKey: s.errKey + 1 })); }
  render() {
    return (
      <React.Fragment key={this.state.errKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState("");
  // password removed — using OTP flow
  const [phone, setPhone] = useState("");
  const [countryDialCode, setCountryDialCode] = useState("+91");
  const [countryName, setCountryName] = useState("India");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpResponse, setOtpResponse] = useState<any>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { error } = useAuth();
  const navigate = useNavigate();
  const { setUserData } = useAuthContext();

  const getOrCreateDeviceId = () => {
    try {
      let did = localStorage.getItem("device_id");
      if (!did) {
        did = 'web-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2,10);
        localStorage.setItem("device_id", did);
      }
      return did;
    } catch (e) {
      return 'web-' + Date.now();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpSent && otp) {
      console.log("OTP entered:", otp);
      return;
    }
  };

  const showEmailField = countryDialCode !== "+91";

  const sendOtp = async () => {
    setSending(true);
    try {
      const phoneToSend = phone && phone.startsWith("+") ? phone : `+${phone}`;
      const payload: any = { phone: phoneToSend };
      if (showEmailField && email) payload.email = email;

      const res = await post("send-otp", payload);
      setOtpResponse(res);
      setOtpSent(true);
    } catch (err: any) {
      console.error("sendOtp error", err);
      setOtpResponse({ success: false, message: err?.message || String(err) });
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      const phoneToSend = phone && phone.startsWith("+") ? phone : `+${phone}`;
      const payload: any = {
        phone: phoneToSend,
        otp,
        device_id: getOrCreateDeviceId(),
        device_name: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : 'web',
        app_version: '1.0.0'
      };

      const res = await post("verify-otp", payload);
      if (res && res.success && res.user) {
        try {
          if (res.token) localStorage.setItem('AUTH_TOKEN', res.token);
          if (res.loginHistoryId) localStorage.setItem('loginHistoryId', res.loginHistoryId);
        } catch (e) {
          console.warn('Could not persist login info', e);
        }

        // Update AuthContext's in-memory state synchronously, not just
        // localStorage — ProtectedRoute reads role from context, and that
        // state doesn't pick up a plain localStorage write on its own. Without
        // this, the very next navigate() below could redirect to /dashboard
        // only to immediately get bounced back to /user-dashboard because the
        // role-gated route still sees the old (logged-out) context state.
        if (res.user) setUserData(res.user);

         const isAdmin = res?.user?.isAdmin;
         if (isAdmin) {
          navigate('/dashboard');
        }else{
           navigate('/user-dashboard');
        }
      } else {
        console.log('1111::: ', 1111);
        navigate('/signup', { state: { phone: phoneToSend, email } });
      }
    } catch (err: any) {
      console.error('verifyOtp error', err);
    } finally {
      setVerifying(false);
    }
  };

  const onPhoneChange = (value: string, country?: any) => {
    setPhone(value);
    if (country && country.dialCode) setCountryDialCode("+" + country.dialCode);
    else {
      const m = value.match(/^\+?(\d{1,3})/);
      if (m) setCountryDialCode("+" + m[1]);
    }
  };

  return (
   <div className="space-y-6">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
            <p className="font-medium text-red-800">Login Error</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Phone</label>
          <div className="mt-1">
            <PhoneInputBoundary>
              <PhoneInput
                country={'in'}
                value={phone}
                onChange={(value: string, data: any) => {
                  try {
                    setPhone(value);
                    const dial = data && data.dialCode ? ('+' + data.dialCode) : '+'.concat((value || '').match(/^\+?(\d{1,3})/)?.[1] || '');
                    setCountryDialCode(dial);
                    setCountryName(data && data.name ? data.name : countryName);
                    if (otpSent) {
                      setOtpSent(false);
                      setOtp("");
                      setOtpResponse(null);
                    }
                  } catch { /* ignore autofill library errors */ }
                }}
                inputProps={{ autoComplete: 'new-password' }}
                inputClass="w-full"
                containerStyle={{ width: '100%' }}
                inputStyle={{ width: '100%' }}
                enableSearch
              />
            </PhoneInputBoundary>
          </div>
        </div>

        {showEmailField && (
          <InputField
            type="email"
            label="Email address"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            icon={<Mail className="text-gray-400" size={18} />}
          />
        )}

        {!otpSent ? (
          <div className="pt-2">
            <Button
              type="button"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              onClick={sendOtp}
              disabled={!phone || (showEmailField && !email) || sending}
            >
              {sending ? 'Sending...' : 'Send OTP'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-green-700">{otpResponse?.message || (otpResponse?.success ? 'OTP sent' : '')}</div>
            <InputField
              type="text"
              label="Enter OTP"
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
            />
            <div className="pt-2">
              <Button
                type="button"
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg"
                onClick={verifyOtp}
                disabled={!otp || verifying}
              >
                {verifying ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </div>
          </div>
        )}

        {/* <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-purple-600 hover:text-purple-500 transition-colors duration-200"
            >
              Forgot password?
            </Link>
          </div>
        </div> */}


        {/* <div className="text-sm text-center pt-4 border-t border-gray-200">
          <span className="text-gray-600">Don't have an account? </span>
          <Link to="/signup" className="font-medium text-purple-600 hover:text-purple-500 transition-colors duration-200">
            Sign up
          </Link>
        </div> */}
      </form>
    </div>
  );
};

export default LoginForm;